(() => {
  const escapeHtml = (value) => String(value ?? '').replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;').replaceAll('"', '&quot;').replaceAll("'", '&#039;');
  const rootPath = () => window.Shopify?.routes?.root || '/';
  const money = (amount, currency = 'GBP') => new Intl.NumberFormat('en-GB', { style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 2 }).format((amount || 0) / 100);
  const hydrateVideo = (video) => {
    if (!(video instanceof HTMLVideoElement) || video.dataset.esVideoHydrated === 'true') return;
    if (!video.getAttribute('poster') && video.dataset.poster) video.poster = video.dataset.poster;
    let attachedSource = false;
    video.querySelectorAll('source[data-src]').forEach((source) => {
      source.src = source.dataset.src;
      source.removeAttribute('data-src');
      attachedSource = true;
    });
    video.dataset.esVideoHydrated = 'true';
    if (attachedSource) video.load();
  };

  const initialiseProduct = (root) => {
    if (root.dataset.robeInitialised === 'true') return;
    root.dataset.robeInitialised = 'true';
    const slides = [...root.querySelectorAll('[data-robe-gallery-item]')];
    const tabs = [...root.querySelectorAll('[data-robe-gallery-thumb]')];
    const stage = root.querySelector('[data-robe-gallery-stage]');
    const label = root.querySelector('[data-robe-gallery-label]');
    let activeIndex = 0;
    const showSlide = (index, moveFocus = false) => {
      if (!slides[index]) return;
      activeIndex = index;
      slides.forEach((slide, slideIndex) => {
        const active = slideIndex === index;
        slide.hidden = !active;
        slide.classList.toggle('active', active);
        const video = slide.querySelector('video');
        if (active) {
          hydrateVideo(video);
          video?.play().catch(() => undefined);
        }
        else video?.pause();
      });
      tabs.forEach((tab, tabIndex) => {
        const active = tabIndex === index;
        tab.classList.toggle('active', active);
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
      });
      if (stage && tabs[index]?.id) stage.setAttribute('aria-labelledby', tabs[index].id);
      if (label) label.textContent = `${String(index + 1).padStart(2, '0')} / ${String(slides.length).padStart(2, '0')}`;
      if (moveFocus) tabs[index]?.focus();
    };
    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => showSlide(index));
      tab.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        let next = index;
        if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
        if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = tabs.length - 1;
        showSlide(next, true);
      });
    });
    let touchStart = 0;
    stage?.addEventListener('touchstart', (event) => { touchStart = event.changedTouches[0]?.clientX || 0; }, { passive: true });
    stage?.addEventListener('touchend', (event) => {
      const distance = (event.changedTouches[0]?.clientX || 0) - touchStart;
      if (Math.abs(distance) < 45) return;
      showSlide(distance < 0 ? (activeIndex + 1) % slides.length : (activeIndex - 1 + slides.length) % slides.length);
    }, { passive: true });

    const technique = root.querySelector('[data-robe-technique-overlay]');
    const techniqueOpen = root.querySelector('[data-robe-technique-open]');
    const techniqueClose = root.querySelector('[data-robe-technique-close]');
    const drawer = root.querySelector('[data-robe-bag-drawer]');
    const scrim = root.querySelector('[data-robe-bag-scrim]');
    const bagClose = root.querySelector('[data-robe-bag-close]');
    const cartBody = root.querySelector('[data-robe-cart-body]');
    let returnFocus = null;
    const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const trapFocus = (container, event) => {
      const focusable = [...container.querySelectorAll(focusableSelector)].filter((element) => element.getClientRects().length > 0);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    const lockPage = (locked) => { document.body.style.overflow = locked ? 'hidden' : ''; };
    const closeTechnique = () => {
      if (!technique || technique.hidden) return;
      technique.hidden = true;
      technique.querySelector('video')?.pause();
      techniqueOpen?.setAttribute('aria-expanded', 'false');
      lockPage(false);
      returnFocus?.focus();
    };
    const openTechnique = () => {
      if (!technique) return;
      returnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      technique.hidden = false;
      techniqueOpen?.setAttribute('aria-expanded', 'true');
      lockPage(true);
      const techniqueVideo = technique.querySelector('video');
      hydrateVideo(techniqueVideo);
      techniqueVideo?.play().catch(() => undefined);
      techniqueClose?.focus();
    };
    techniqueOpen?.addEventListener('click', openTechnique);
    techniqueClose?.addEventListener('click', closeTechnique);
    technique?.addEventListener('click', (event) => { if (event.target === technique) closeTechnique(); });

    const form = root.querySelector('#RitualRobeProductForm');
    const addButton = root.querySelector('[data-robe-add-button]');
    const purchaseButtons = [...document.querySelectorAll('[data-robe-add-button], [data-robe-add-trigger]')];
    const updateBagCount = (count) => {
      document.querySelectorAll('[data-es-cart-count], [data-bag-count]').forEach((item) => { item.textContent = `(${count})`; });
      document.querySelectorAll('.header-action--right[href*="/cart"]').forEach((item) => item.setAttribute('aria-label', `Open bag with ${count} items`));
    };
    const renderCart = (cart) => {
      updateBagCount(cart.item_count);
      if (!cartBody) return;
      if (!cart.items.length) { cartBody.innerHTML = '<div class="empty-bag"><p>Your Bag Is Empty.</p></div>'; return; }
      const items = cart.items.map((item) => {
        const orderType = item.properties?.['Order type'];
        const dispatch = orderType ? `<p class="bag-product-dispatch">${escapeHtml(orderType)}</p>` : '';
        return `<div class="bag-product">${item.image ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.product_title)}" width="160" height="200">` : ''}<div><p class="bag-product-name">${escapeHtml(item.product_title)}</p>${dispatch}<p>Quantity ${item.quantity}</p><p>${money(item.final_line_price, cart.currency)}</p></div><button type="button" data-robe-remove-key="${escapeHtml(item.key)}">Remove</button></div>`;
      }).join('');
      const hasPreOrder = cart.items.some((item) => item.properties?.['Order type']);
      const note = hasPreOrder ? 'Pre-order items dispatch in October 2026' : 'Complimentary Tracked UK Delivery';
      cartBody.innerHTML = `${items}<div class="drawer-total"><div><span>Subtotal</span><span>${money(cart.total_price, cart.currency)}</span></div><p>${note}</p><a href="${rootPath()}checkout">Secure Checkout</a></div>`;
      cartBody.querySelectorAll('[data-robe-remove-key]').forEach((button) => button.addEventListener('click', async () => {
        button.disabled = true;
        const response = await fetch(`${rootPath()}cart/change.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: button.dataset.robeRemoveKey, quantity: 0 }) });
        if (response.ok) renderCart(await response.json()); else button.disabled = false;
      }));
    };
    const closeBag = () => {
      if (!drawer || !scrim || drawer.hidden) return;
      drawer.classList.remove('open'); scrim.classList.remove('visible'); drawer.hidden = true; scrim.hidden = true; lockPage(false); returnFocus?.focus();
    };
    const openBag = async ({ refresh = true } = {}) => {
      if (!drawer || !scrim) return;
      returnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      drawer.hidden = false; scrim.hidden = false;
      requestAnimationFrame(() => { drawer.classList.add('open'); scrim.classList.add('visible'); });
      lockPage(true); bagClose?.focus();
      if (refresh) {
        try {
          const response = await fetch(`${rootPath()}cart.js`, { headers: { Accept: 'application/json' } });
          if (response.ok) renderCart(await response.json());
        } catch (_) { /* Retain the loading state if Shopify is unavailable. */ }
      }
    };
    bagClose?.addEventListener('click', closeBag);
    scrim?.addEventListener('click', closeBag);
    const ensureVariantId = () => {
      const variantInput = form?.querySelector('input[name="id"]');
      if (variantInput instanceof HTMLInputElement && !variantInput.value && root.dataset.variantId) {
        variantInput.value = root.dataset.variantId;
      }
      return variantInput instanceof HTMLInputElement && variantInput.value !== '';
    };
    const setPurchaseBusy = (busy) => purchaseButtons.forEach((button) => {
      if (busy) {
        button.dataset.originalLabel = button.textContent || '';
        button.dataset.originalDisabled = String(button.disabled);
        button.disabled = true;
        button.textContent = 'Adding…';
      } else {
        button.disabled = button.dataset.originalDisabled === 'true';
        button.textContent = button.dataset.originalLabel || button.textContent || '';
        delete button.dataset.originalLabel;
        delete button.dataset.originalDisabled;
      }
    });
    form?.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!(form instanceof HTMLFormElement) || addButton?.disabled) return;
      if (!ensureVariantId()) {
        window.alert('This product option is temporarily unavailable. Please refresh the page and try again.');
        return;
      }
      setPurchaseBusy(true);
      closeTechnique();
      if (cartBody) cartBody.innerHTML = '<div class="empty-bag"><p>Adding To Your Bag…</p></div>';
      await openBag({ refresh: false });
      let added = false;
      try {
        const response = await fetch(`${rootPath()}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
        if (!response.ok) { const error = await response.json().catch(() => ({})); throw new Error(error.description || 'This item could not be added.'); }
        added = true;
        const cartResponse = await fetch(`${rootPath()}cart.js`, { headers: { Accept: 'application/json' } });
        if (!cartResponse.ok) throw new Error('The item was added, but the bag could not be refreshed.');
        renderCart(await cartResponse.json());
      } catch (error) {
        if (error instanceof TypeError && !added) {
          closeBag();
          form.submit();
          return;
        }
        if (added && cartBody) {
          cartBody.innerHTML = `<div class="empty-bag"><p>Added To Your Bag.</p><a href="${rootPath()}cart">View Bag</a></div>`;
          return;
        }
        closeBag();
        window.alert(error instanceof Error ? error.message : 'This item could not be added.');
      } finally { setPurchaseBusy(false); }
    });
    document.addEventListener('keydown', (event) => {
      const activeDialog = technique && !technique.hidden ? technique : drawer && !drawer.hidden ? drawer : null;
      if (event.key === 'Tab' && activeDialog) trapFocus(activeDialog, event);
      if (event.key === 'Escape') { if (technique && !technique.hidden) closeTechnique(); else closeBag(); }
    });
    if ('IntersectionObserver' in window) {
      const videoObserver = new IntersectionObserver((entries) => entries.forEach((entry) => {
        if (entry.isIntersecting && !entry.target.closest('[hidden]')) {
          hydrateVideo(entry.target);
          entry.target.play().catch(() => undefined);
        } else entry.target.pause();
      }), { rootMargin: '320px 0px' });
      root.querySelectorAll('[data-robe-deferred-video]').forEach((video) => videoObserver.observe(video));
    } else {
      root.querySelectorAll('[data-robe-deferred-video]').forEach(hydrateVideo);
    }
    showSlide(0);
  };

  const initialisePageControls = (scope = document) => {
    scope.querySelectorAll('[data-robe-accordion]').forEach((button) => {
      if (button.dataset.robeAccordionInitialised) return;
      button.dataset.robeAccordionInitialised = 'true';
      button.addEventListener('click', () => {
        const body = button.nextElementSibling;
        const open = button.getAttribute('aria-expanded') === 'true';
        button.setAttribute('aria-expanded', String(!open));
        if (button.lastElementChild) button.lastElementChild.textContent = open ? '+' : '−';
        if (body instanceof HTMLElement) body.hidden = open;
      });
    });
    scope.querySelectorAll('[data-robe-add-trigger]').forEach((trigger) => {
      if (trigger.dataset.robeAddInitialised) return;
      trigger.dataset.robeAddInitialised = 'true';
      trigger.addEventListener('click', () => document.getElementById('RitualRobeProductForm')?.requestSubmit());
    });
    scope.querySelectorAll('.es-robe-reviews').forEach((reviewSection) => {
      const labelReviews = () => reviewSection.querySelectorAll('.jdgm-rev').forEach((review) => {
        if (review.querySelector('.es-verified-customer')) return;
        const label = document.createElement('span'); label.className = 'verified-customer es-verified-customer'; label.textContent = 'Verified Customer';
        (review.querySelector('.jdgm-rev__author-wrapper') || review).append(label);
      });
      labelReviews();
      if (!reviewSection.dataset.robeReviewObserver) {
        reviewSection.dataset.robeReviewObserver = 'true';
        new MutationObserver(labelReviews).observe(reviewSection, { childList: true, subtree: true });
      }
    });
    scope.querySelectorAll('[data-robe-closing]').forEach((closing) => {
      if (closing.dataset.robeClosingInitialised) return;
      closing.dataset.robeClosingInitialised = 'true';
      const sticky = closing.querySelector('[data-robe-sticky-purchase]');
      const finalPurchase = closing.querySelector('[data-robe-final-purchase]');
      const updateSticky = () => {
        const heroButton = document.querySelector('[data-robe-pdp] [data-robe-add-button]');
        if (!sticky || !heroButton) return;
        const heroPassed = heroButton.getBoundingClientRect().bottom < 0;
        const finalVisible = finalPurchase ? finalPurchase.getBoundingClientRect().top < window.innerHeight : false;
        sticky.hidden = !heroPassed || finalVisible;
      };
      updateSticky();
      document.addEventListener('scroll', updateSticky, { passive: true });
      window.addEventListener('resize', updateSticky, { passive: true });
    });
  };

  const boot = (scope = document) => { scope.querySelectorAll('[data-robe-pdp]').forEach(initialiseProduct); initialisePageControls(scope); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(), { once: true }); else boot();
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();
